import React, { useState } from "react";
import { Box, IconButton, Link, Modal, Typography, alpha } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VideoMessage from "../../../components/MessageHistory/VideoMessage";
import TeamAudioMessage from "./TeamAudioMessage";

const TeamMessageAttachment = ({ message, isOwn = false, onAudioPlayed }) => {
  const [imageError, setImageError] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const type = message?.messageType;
  const imageUrl = message?.imageUrl;
  const videoUrl = message?.videoUrl;
  const audioUrl = message?.audioUrl;
  const fileUrl = message?.fileUrl;
  const fileName = message?.fileName;

  const fileCardSx = (theme) => ({
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    px: 1.5,
    py: 1.25,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    bgcolor: isOwn
      ? alpha(theme.palette.common.white, 0.95)
      : theme.palette.background.paper,
    color: theme.palette.text.primary,
    textDecoration: "none",
    minWidth: 0,
    maxWidth: "100%",
    transition: "background-color 0.15s",
    "&:hover": {
      bgcolor: isOwn
        ? theme.palette.common.white
        : theme.palette.action.hover,
    },
  });

  if (type === "image" || imageUrl) {
    if (!imageUrl) return null;
    return (
      <>
        {imageError ? (
          <ImageNotSupportedIcon sx={{ fontSize: 60, color: "gray" }} />
        ) : (
          <Box
            component="img"
            src={imageUrl}
            alt="Imagem enviada"
            sx={{
              width: "100%",
              maxWidth: "100%",
              height: "auto",
              maxHeight: 320,
              borderRadius: 2,
              display: "block",
              objectFit: "contain",
              cursor: "pointer",
              border: (t) => `1px solid ${t.palette.divider}`,
            }}
            onClick={() => setImagePreviewOpen(true)}
            onError={() => setImageError(true)}
          />
        )}

        <Modal
          open={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          aria-label="Visualização da imagem"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 1.5, sm: 2 },
            boxSizing: "border-box",
          }}
          slotProps={{
            backdrop: {
              sx: { bgcolor: alpha("#000", 0.88) },
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              maxWidth: "100dvw",
              maxHeight: "100dvh",
              outline: "none",
              boxSizing: "border-box",
            }}
            onClick={() => setImagePreviewOpen(false)}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setImagePreviewOpen(false);
              }}
              aria-label="Fechar imagem"
              sx={{
                position: "fixed",
                top: 12,
                right: 12,
                zIndex: 1,
                color: "common.white",
                bgcolor: alpha("#000", 0.45),
                "&:hover": { bgcolor: alpha("#000", 0.65) },
              }}
            >
              <CloseIcon />
            </IconButton>
            {!imageError && (
              <Box
                component="img"
                src={imageUrl}
                alt="Imagem enviada"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  maxWidth: "calc(100dvw - 24px)",
                  maxHeight: "calc(100dvh - 24px)",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                  borderRadius: 1,
                  boxShadow: 8,
                  userSelect: "none",
                }}
                onError={() => setImageError(true)}
              />
            )}
          </Box>
        </Modal>
      </>
    );
  }

  if (type === "video" || videoUrl) {
    if (!videoUrl) return null;
    return (
      <Box sx={{ maxWidth: "100%" }}>
        <VideoMessage message={{ video: videoUrl, url: videoUrl }} />
      </Box>
    );
  }

  if (type === "audio" || audioUrl) {
    if (!audioUrl) return null;
    return (
      <TeamAudioMessage
        audioUrl={audioUrl}
        isOwn={isOwn}
        message={message}
        onAudioPlayed={onAudioPlayed}
      />
    );
  }

  if (type === "file" || fileUrl) {
    if (!fileUrl) return null;
    return (
      <Box
        component={Link}
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        underline="none"
        sx={fileCardSx}
      >
        <Box
          sx={(theme) => ({
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
            flexShrink: 0,
          })}
        >
          <InsertDriveFileIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {fileName || "Arquivo"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Clique para abrir
          </Typography>
        </Box>
      </Box>
    );
  }

  return null;
};

export default TeamMessageAttachment;
